import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import PortfolioTable from './components/PortfolioTable/PortfolioTable';
import { ProductData } from './types';
import { hideGlobalTooltip, showGlobalTooltip } from './utils';
import GlobalTooltip from './components/GlobalTooltip';
import usePortfolioSort from './hooks/usePortfolioSort';

import PortfolioConfigData from 'config/portfolio-config.json';
import { HIDDEN_COLUMNS, filterColumns, filterMappings } from 'config/portfolio-hidden-columns';
import colorsStore from 'shared/store/colorsStore';
import styles from './finecoPortfolioCompositionArtifact.module.css';
import { formatItalianNumber } from 'utils/formatUtils';
import EfficientFrontierContainer from '../fineco-portfolio/components/frontiera-efficiente/EfficientFrontierContainer';
import CaratteristicheDiPortafoglioContainer from '../fineco-portfolio/components/caratteristiche-di-portafoglio/CaratteristicheDiPortafoglioContainer';
import IndicatoriFinanziariContainer from '../fineco-portfolio/components/indicatori-finanziari/IndicatoriFinanziariContainer';
import { ef_data } from '../fineco-portfolio/FinecoConfigData';
import {
  POLIZZE_COLUMN_LABELS,
  POLIZZE_SUBTOTAL_LABEL,
  polizzaTipoProdotto,
  polizzeSubTitle,
  POLIZZE_TITLE,
} from '../fineco-portfolio/components/composizione-del-portafoglio/components/polizze';

import FinecoFunds from 'shared/assets/svg/FinecoFunds';
import FinecoRefresh from 'shared/assets/svg/FinecoRefresh';
import FinecoContoDeposito from 'shared/assets/svg/FinecoContoDeposito';
import FinecoMaxiPrelievo from 'shared/assets/svg/FinecoMaxiPrelievo';
import { useFeeType } from 'pages/assistant/hooks/useFeeType';
import activityPanelStore from 'shared/store/activityPanelStore';
import { useStreetBeatChat } from '@dev-streetbeat/chat/react';
import {
  FINECO_REBALANCING_SYNTHETIC_QUESTIONS,
  type FinecoRebalancingStarterId,
} from './FinecoRebalancingSyntheticQuestions';
import { buildDiagnosiMessages } from './finecoDiagnosiMessages';
import { notifySendFailure } from 'pages/assistant/components/chat/utils/sendFailureToast';
import { createLogger } from 'shared/logger';

const log = createLogger('PortfolioComposition');

// After an action pill click, further clicks on the card's pills are ignored
// until the seed/send settles and at least this long has passed: a double
// click (~100-300 ms) can outrun the seed HTTP call and seed the starter twice.
const ACTION_CLICK_GUARD_MS = 1000;

// ISIN columns are 152px (not 130) so the FAM FoF expand toggle fits inline
// with the full ISIN, as in the portfolio artifact's tables (issue #90).
// The held listing's market is not a column (Fineco's approved layout): it
// shows in the ISIN and Nome tooltips instead.

const BASE_COLUMN_WIDTHS_FONDI = [
  152, 300, 85, 100, 80, 100, 110, 100, 150, 120, 120,
  200, 80, 100, 90, 150, 100, 140, 120, 180,
]; // 20 columns: ISIN, Nome, PAC, Tipo prodotto, Divisa, Peso, Controvalore, FS, FS Advice, P&L €, P&L %, Microcategoria, YTM, ESG, Duration, Commissioni, Abbuoni, Costi, Div yield, Freq div

const BASE_COLUMN_WIDTHS_BONDS = [
  152, 300, 80, 100, 110, 120, 120, 200, 90, 100,
  80, 100, 150, 100, 100, 110, 120, 180,
]; // 18 columns

const BASE_COLUMN_WIDTHS_STOCKS = [
  152, 300, 80, 180, 167, 195, 195, 345, 168, 195, 335,
]; // 11 columns

const BASE_COLUMN_WIDTHS_CERTIFICATI = [
  152, 300, 80, 100, 120, 120, 120, 200, 200, 120,
  120, 140, 180, 220, 130, 140, 130, 140,
]; // 18 columns: ISIN, Nome, Divisa, Peso, Controvalore, P&L €, P&L %, Emittente, Microcategoria, Data emissione, Data scadenza, Costi totali, Categoria Acepi, Tipologia, Protezione, Barriera down, Opz. quanto, Div yield

const BASE_COLUMN_WIDTHS_POLIZZE = [
  152, 300, 100, 80, 100, 110, 200, 80, 100, 90, 150, 140,
]; // 12 columns, widths as in the funds table: Codice prodotto, Nome, Tipo prodotto, Divisa, Peso, Controvalore, Microcategoria, YTM, ESG, Duration, Commissioni, Costi

const HEADERS_FONDI = [
  'isin', 'nome', 'investment_type', 'tipo_prodotto', 'valuta', 'peso_sul_ptf', 'controvalore',
  'final_score', 'final_score_advice', 'pl_fiscale_euro', 'pl_fiscale_perc',
  'microcategoria_advice', 'ytm', 'rating_ESG', 'duration',
  'commissioni_gestione', 'abbuoni', 'costi_totali_stimati',
  'dividend_yield', 'frequenza_distribuzione_dividendo',
];

const HEADERS_BONDS = [
  'isin', 'nome', 'valuta', 'peso_sul_ptf', 'controvalore',
  'pl_fiscale_euro', 'pl_fiscale_perc', 'microcategoria_advice', 'duration',
  'rateo_euro', 'ytm', 'rating_ESG', 'tipo_emittente', 'tipo_bond',
  'paese', 'scadenza', 'dividend_yield', 'frequenza_distribuzione_dividendo',
];

const HEADERS_STOCKS = [
  'isin', 'nome', 'valuta', 'controvalore', 'peso_sul_ptf',
  'pl_fiscale_euro', 'pl_fiscale_perc', 'microcategoria_advice',
  'rating_ESG', 'dividend_yield', 'frequenza_distribuzione_dividendo',
];

const HEADERS_CERTIFICATI = [
  'isin', 'nome', 'valuta', 'peso_sul_ptf', 'controvalore',
  'pl_fiscale_euro', 'pl_fiscale_perc', 'emittente', 'microcategoria_advice',
  'data_emissione', 'data_scadenza', 'costi_totali_stimati',
  'fida_categoria_acepi', 'fida_tipologia', 'fida_protezione',
  'fida_barriera_down', 'fida_quanto', 'dividend_yield',
];

const HEADERS_POLIZZE = Object.keys(POLIZZE_COLUMN_LABELS);

const SORTABLE_COLUMNS_FONDI = [
  'peso_sul_ptf', 'controvalore', 'final_score', 'final_score_advice',
  'pl_fiscale_euro', 'pl_fiscale_perc', 'microcategoria_advice', 'ytm',
  'rating_ESG', 'duration', 'commissioni_gestione', 'abbuoni',
  'costi_totali_stimati', 'dividend_yield', 'frequenza_distribuzione_dividendo',
];

const SORTABLE_COLUMNS_BONDS = [
  'peso_sul_ptf', 'controvalore', 'pl_fiscale_euro', 'pl_fiscale_perc',
  'microcategoria_advice', 'duration', 'rateo_euro', 'ytm', 'rating_ESG',
  'tipo_emittente', 'tipo_bond', 'paese', 'scadenza',
  'dividend_yield', 'frequenza_distribuzione_dividendo',
];

const SORTABLE_COLUMNS_STOCKS = [
  'peso_sul_ptf', 'pl_fiscale_euro', 'pl_fiscale_perc',
  'microcategoria_advice', 'rating_ESG', 'dividend_yield',
  'frequenza_distribuzione_dividendo',
];

const SORTABLE_COLUMNS_CERTIFICATI = [
  'controvalore', 'peso_sul_ptf', 'pl_fiscale_euro', 'pl_fiscale_perc',
  'emittente', 'microcategoria_advice', 'data_emissione', 'data_scadenza',
  'costi_totali_stimati', 'fida_categoria_acepi', 'fida_tipologia',
  'fida_protezione', 'fida_barriera_down', 'fida_quanto', 'dividend_yield',
];

const SORTABLE_COLUMNS_POLIZZE = [
  'peso_sul_ptf', 'controvalore', 'microcategoria_advice', 'ytm',
  'rating_ESG', 'duration', 'commissioni_gestione', 'costi_totali_stimati',
];

// Apply P&L column filtering
const FILTERED_BONDS = filterColumns(HEADERS_BONDS, BASE_COLUMN_WIDTHS_BONDS);
const FILTERED_STOCKS = filterColumns(HEADERS_STOCKS, BASE_COLUMN_WIDTHS_STOCKS);
const FILTERED_CERTIFICATI = filterColumns(HEADERS_CERTIFICATI, BASE_COLUMN_WIDTHS_CERTIFICATI);
const FILTERED_POLIZZE = filterColumns(HEADERS_POLIZZE, BASE_COLUMN_WIDTHS_POLIZZE);

const FILTERED_SORTABLE_BONDS = SORTABLE_COLUMNS_BONDS.filter((c) => !HIDDEN_COLUMNS.has(c));
const FILTERED_SORTABLE_STOCKS = SORTABLE_COLUMNS_STOCKS.filter((c) => !HIDDEN_COLUMNS.has(c));
const FILTERED_SORTABLE_CERTIFICATI = SORTABLE_COLUMNS_CERTIFICATI.filter((c) => !HIDDEN_COLUMNS.has(c));
const FILTERED_SORTABLE_POLIZZE = SORTABLE_COLUMNS_POLIZZE.filter((c) => !HIDDEN_COLUMNS.has(c));

const COLUMN_MAPPINGS_BONDS = filterMappings({
  isin: 'ISIN', nome: 'Nome', valuta: 'Divisa', peso_sul_ptf: 'Peso',
  controvalore: 'Controvalore', pl_fiscale_euro: 'P&L fiscale \u20AC',
  pl_fiscale_perc: 'P&L fiscale %', microcategoria_advice: 'Microcategoria',
  duration: 'Duration', rateo_euro: 'Rateo \u20AC', ytm: 'YTM',
  rating_ESG: 'Rating ESG', tipo_emittente: 'Tipo emittente',
  tipo_bond: 'Tipo bond', paese: 'Paese', scadenza: 'Scadenza',
  dividend_yield: 'Dividend yield',
  frequenza_distribuzione_dividendo: 'Frequenza distribuzione cedole',
});

const COLUMN_MAPPINGS_STOCKS = filterMappings({
  isin: 'ISIN', nome: 'Nome', valuta: 'Divisa',
  controvalore: 'Controvalore', peso_sul_ptf: 'Peso',
  pl_fiscale_euro: 'P&L fiscale \u20AC', pl_fiscale_perc: 'P&L fiscale %',
  microcategoria_advice: 'Microcategoria', rating_ESG: 'Rating ESG',
  dividend_yield: 'Dividend yield',
  frequenza_distribuzione_dividendo: 'Frequenza distribuzione dividendo',
});

const COLUMN_MAPPINGS_CERTIFICATI = filterMappings({
  isin: 'ISIN', nome: 'Nome', valuta: 'Divisa',
  controvalore: 'Controvalore', peso_sul_ptf: 'Peso',
  pl_fiscale_euro: 'P&L fiscale \u20AC', pl_fiscale_perc: 'P&L fiscale %',
  emittente: 'Emittente', microcategoria_advice: 'Microcategoria',
  data_emissione: 'Data emissione', data_scadenza: 'Data scadenza',
  costi_totali_stimati: 'Costi totali',
  fida_categoria_acepi: 'Categoria Acepi', fida_tipologia: 'Tipologia',
  fida_protezione: 'Protezione', fida_barriera_down: 'Barriera down',
  fida_quanto: 'Opzione quanto', dividend_yield: 'Dividend yield',
});

const COLUMN_MAPPINGS_POLIZZE = filterMappings(POLIZZE_COLUMN_LABELS);

// «Mercato: XETRA» for the ISIN and Nome tooltips (ticket 150); none for
// payloads without the field.
const marketTooltip = (asset: any): string | undefined => {
  if (asset.listing_status === 'not_in_perimeter') {
    return `Mercato: ${asset.market ?? '-'} (non nel perimetro Fineco)`;
  }
  return asset.market ? `Mercato: ${asset.market}` : undefined;
};

type Props = {
  data: any;
  onSend: (text: string) => void;
  onLoaded?: () => void;
};

const FinecoPortfolioCompositionArtifact = ({ data, onSend, onLoaded }: Props) => {
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);
  const feeType = useFeeType();
  const constraintsPanelMode = activityPanelStore((state) => state.constraintsPanelMode);
  const isRebalancing = constraintsPanelMode !== 'builder';

  const fsColumnLabel = useMemo(() => {
    if (!isRebalancing || !feeType) return 'FS / FS Advice';
    if (feeType === 'feeOnTop') return 'Final Score';
    if (feeType === 'feeOnly') return 'Final Score Advice';
    return 'FS / FS Advice';
  }, [isRebalancing, feeType]);

  // Dynamic column hiding: P&L columns + fee-type-based FS column exclusion
  const dynamicHidden = useMemo(() => {
    const hidden = new Set(HIDDEN_COLUMNS);
    if (isRebalancing && feeType === 'feeOnTop') hidden.add('final_score_advice');
    if (isRebalancing && feeType === 'feeOnly') hidden.add('final_score');
    return hidden;
  }, [isRebalancing, feeType]);

  // Re-filter headers/widths/sortable with dynamic hidden set
  const filteredFondi = useMemo(() => {
    const h: string[] = [], w: number[] = [];
    HEADERS_FONDI.forEach((header, i) => { if (!dynamicHidden.has(header)) { h.push(header); w.push(BASE_COLUMN_WIDTHS_FONDI[i]); } });
    return { headers: h, widths: w };
  }, [dynamicHidden]);
  const filteredSortableFondi = useMemo(() => SORTABLE_COLUMNS_FONDI.filter((c) => !dynamicHidden.has(c)), [dynamicHidden]);

  useEffect(() => {
    onLoaded?.();
  }, [onLoaded]);

  // Transform assets data to ProductData format
  const transformedData = useMemo((): ProductData[] => {
    if (!data?.data?.assets || !Array.isArray(data.data.assets)) {
      return [];
    }

    return data.data.assets.map((asset: any) => ({
      isin: asset.isin,
      tipo_prodotto: asset.instrument_type,
      nome: asset.name,
      investment_type: asset.investment_type === 'PAC' ? 'PAC' : 'PIC',
      valuta: asset.valuta,
      market_tooltip: marketTooltip(asset),
      controvalore: asset.value ? formatItalianNumber(asset.value) : '0,00',
      peso_sul_ptf: asset.weight ? formatItalianNumber(asset.weight * 100) : '0,00',
      microcategoria_advice: asset.microcategory_name || '-',
      costi_totali_stimati: asset.costi_totali_stimati != null ? formatItalianNumber(asset.costi_totali_stimati) : '-',
      final_score: asset.final_score ?? '-',
      final_score_advice: asset.final_score_advice ?? '-',
      rating_ESG: asset.ranking_esg || '-',
      instrument_type: asset.instrument_type,
      // Bond-specific fields
      emittente: asset.emittente ?? '-',
      tipo_emittente: asset.emittente ?? '-',
      tipo_bond: asset.tipo_bond ?? '-',
      paese: asset.paese ?? '-',
      scadenza: asset.scadenza ? asset.scadenza.split('-').reverse().join('/') : '-',
      scadenza_raw: asset.scadenza ?? '',
      ytm: asset.ytm != null ? formatItalianNumber(asset.ytm) : '-',
      duration: asset.duration != null ? formatItalianNumber(asset.duration) : '-',
      // New enrichment fields
      commissioni_gestione: asset.commissioni_gestione != null ? formatItalianNumber(asset.commissioni_gestione) : '-',
      abbuoni: asset.abbuoni != null ? formatItalianNumber(asset.abbuoni) : '-',
      dividend_yield: asset.dividend_yield != null ? formatItalianNumber(asset.dividend_yield) : '-',
      frequenza_distribuzione_dividendo: asset.frequenza_distribuzione_dividendo || '-',
      pl_fiscale_euro: asset.pl_fiscale_euro != null ? formatItalianNumber(asset.pl_fiscale_euro) : '-',
      pl_fiscale_perc: asset.pl_fiscale_perc != null ? formatItalianNumber(asset.pl_fiscale_perc) : '-',
      rateo_euro: asset.rateo_euro != null ? formatItalianNumber(asset.rateo_euro) : '-',
      // Certificate FIDA fields
      data_emissione: asset.data_emissione ? asset.data_emissione.split('-').reverse().join('/') : '-',
      data_scadenza: asset.data_scadenza ? asset.data_scadenza.split('-').reverse().join('/') : '-',
      fida_categoria_acepi: asset.fida_categoria_acepi || '-',
      fida_tipologia: asset.fida_tipologia || '-',
      fida_protezione: asset.fida_protezione != null ? formatItalianNumber(asset.fida_protezione) : '-',
      fida_barriera_down: asset.fida_barriera_down != null ? formatItalianNumber(asset.fida_barriera_down) : '-',
      fida_quanto: asset.fida_quanto || '-',
      // Insurance policies: product code, Tipo prodotto and '-' for missing text cells
      ...(asset.instrument_type?.toLowerCase() === 'polizze' && {
        isin: asset.isin || '-',
        nome: asset.name || '-',
        valuta: asset.valuta || '-',
        tipo_prodotto: polizzaTipoProdotto(asset),
        is_gestione_separata: asset.is_gestione_separata,
      }),
    }));
  }, [data]);

  // Split data into funds/ETFs, bonds, stocks, certificati, polizze
  const fundsEtfData = useMemo(() => {
    return transformedData.filter((item) => {
      const type = item.instrument_type?.toLowerCase();
      return type !== 'bond' && type !== 'azioni' && type !== 'certificati' && type !== 'polizze';
    });
  }, [transformedData]);

  const bondsData = useMemo(() => {
    return transformedData.filter((item) => item.instrument_type?.toLowerCase() === 'bond');
  }, [transformedData]);

  const stocksData = useMemo(() => {
    return transformedData.filter((item) => item.instrument_type?.toLowerCase() === 'azioni');
  }, [transformedData]);

  const certificatiData = useMemo(() => {
    return transformedData.filter((item) => item.instrument_type?.toLowerCase() === 'certificati');
  }, [transformedData]);

  const polizzeData = useMemo(() => {
    return transformedData.filter((item) => item.instrument_type?.toLowerCase() === 'polizze');
  }, [transformedData]);

  // Calculate total controvalore
  const totalControvalore = useMemo(() => {
    const total = transformedData.reduce((sum, item) => {
      // Parse Italian formatted number (replace . thousands separator and , decimal separator)
      const value = parseFloat(item.controvalore.replace(/\./g, '').replace(',', '.')) || 0;
      return sum + value;
    }, 0);
    return total;
  }, [transformedData]);

  // Calculate subtotals
  const fundsEtfSubtotal = useMemo(() => {
    const subtotal = fundsEtfData.reduce((sum, item) => {
      const value = parseFloat(item.controvalore.replace(/\./g, '').replace(',', '.')) || 0;
      return sum + value;
    }, 0);
    return subtotal;
  }, [fundsEtfData]);

  const bondsSubtotal = useMemo(() => {
    const subtotal = bondsData.reduce((sum, item) => {
      const value = parseFloat(item.controvalore.replace(/\./g, '').replace(',', '.')) || 0;
      return sum + value;
    }, 0);
    return subtotal;
  }, [bondsData]);

  const stocksSubtotal = useMemo(() => {
    const subtotal = stocksData.reduce((sum, item) => {
      const value = parseFloat(item.controvalore.replace(/\./g, '').replace(',', '.')) || 0;
      return sum + value;
    }, 0);
    return subtotal;
  }, [stocksData]);

  const certificatiSubtotal = useMemo(() => {
    return certificatiData.reduce((sum, item) => {
      const value = parseFloat(item.controvalore.replace(/\./g, '').replace(',', '.')) || 0;
      return sum + value;
    }, 0);
  }, [certificatiData]);

  const polizzeSubtotal = useMemo(() => {
    return polizzeData.reduce((sum, item) => {
      const value = parseFloat(item.controvalore.replace(/\./g, '').replace(',', '.')) || 0;
      return sum + value;
    }, 0);
  }, [polizzeData]);

  // Format the values for display
  const formattedTotalControvalore = formatItalianNumber(totalControvalore);
  const formattedFundsEtfSubtotal = formatItalianNumber(fundsEtfSubtotal);
  const formattedBondsSubtotal = formatItalianNumber(bondsSubtotal);
  const formattedStocksSubtotal = formatItalianNumber(stocksSubtotal);
  const formattedCertificatiSubtotal = formatItalianNumber(certificatiSubtotal);
  const formattedPolizzeSubtotal = formatItalianNumber(polizzeSubtotal);

  // Set up sorting for all tables
  const {
    sortConfig: fundsEtfSortConfig,
    sortedData: sortedFundsEtfData,
    handleSort: handleFundsEtfSort,
  } = usePortfolioSort({
    data: fundsEtfData,
    sortableColumns: filteredSortableFondi,
  });

  const {
    sortConfig: bondsSortConfig,
    sortedData: sortedBondsData,
    handleSort: handleBondsSort,
  } = usePortfolioSort({
    data: bondsData,
    sortableColumns: FILTERED_SORTABLE_BONDS,
  });

  const {
    sortConfig: stocksSortConfig,
    sortedData: sortedStocksData,
    handleSort: handleStocksSort,
  } = usePortfolioSort({
    data: stocksData,
    sortableColumns: FILTERED_SORTABLE_STOCKS,
  });

  const {
    sortConfig: certificatiSortConfig,
    sortedData: sortedCertificatiData,
    handleSort: handleCertificatiSort,
  } = usePortfolioSort({
    data: certificatiData,
    sortableColumns: FILTERED_SORTABLE_CERTIFICATI,
  });

  const {
    sortConfig: polizzeSortConfig,
    sortedData: sortedPolizzeData,
    handleSort: handlePolizzeSort,
  } = usePortfolioSort({
    data: polizzeData,
    sortableColumns: FILTERED_SORTABLE_POLIZZE,
  });

  const COLUMN_MAPPINGS_FONDI: Record<string, string> = useMemo(() => {
    const mappings: Record<string, string> = {
    isin: 'ISIN', nome: 'Nome', investment_type: 'Tipologia', tipo_prodotto: 'Tipo prodotto', valuta: 'Divisa',
    controvalore: 'Controvalore', peso_sul_ptf: 'Peso',
    final_score: 'Final Score', final_score_advice: fsColumnLabel,
    pl_fiscale_euro: 'P&L fiscale \u20AC', pl_fiscale_perc: 'P&L fiscale %',
    microcategoria_advice: 'Microcategoria', ytm: 'YTM', rating_ESG: 'Rating ESG',
    duration: 'Duration', commissioni_gestione: 'Commissioni di gestione',
    abbuoni: 'Abbuoni', costi_totali_stimati: 'Costi totali stimati',
    dividend_yield: 'Dividend yield', frequenza_distribuzione_dividendo: 'Frequenza distribuzione dividendo',
    };
    dynamicHidden.forEach((col) => delete mappings[col]);
    return mappings;
  }, [fsColumnLabel, dynamicHidden]);

  // Tooltip handlers
  const handleTooltipTrigger = useCallback((event: React.MouseEvent, text: string, label?: string) => {
    showGlobalTooltip(event, text, label);
  }, []);

  const handleTooltipHide = useCallback(() => {
    hideGlobalTooltip();
  }, []);

  // Action items configuration
  const actionItems = useMemo(
    () => [
      {
        id: 'diagnosi',
        text: 'Crea una diagnosi',
        icon: <FinecoFunds className={styles.pillIcon} />,
      },
      {
        id: 'ribilanciare',
        text: 'Ribilancia il dossier',
        icon: <FinecoRefresh className={styles.pillIcon} />,
      },
      {
        id: 'deposit',
        text: 'Ribilancia il dossier con un apporto di liquidità',
        icon: <FinecoContoDeposito className={styles.pillIcon} />,
      },
      {
        id: 'withdraw',
        text: 'Ribilancia il dossier con un prelievo di liquidità',
        icon: <FinecoMaxiPrelievo className={styles.pillIcon} />,
      },
    ],
    [],
  );

  // Build reportData for EfficientFrontier and IndicatoriFinanziari components
  const reportData = useMemo(() => {
    const d = data?.data;
    if (!d?.financial_plan || !d?.financial_indicators) return null;

    const qualityValue = isRebalancing && feeType === 'feeOnly'
      ? d.financial_indicators.quality_final_score_advice
      : d.financial_indicators.quality_final_score;

    return {
      data: {
        financial_plan: d.financial_plan,
        var: d.var,
        return: d.return,
      },
      indicatori_finanziari: [
        { label: 'Efficienza', description: "Indica l'efficienza del portafoglio espressa in termini di rischio/rendimento", value: d.financial_indicators.efficiency },
        { label: 'Qualità', description: 'Indica la qualità del portafoglio determinata sulla base della metodologia* di valutazione definita dalla Banca', value: qualityValue },
        { label: 'Coerenza VaR', description: 'Indica la coerenza del rischio del portafoglio (VaR) rispetto ai limiti previsti per il piano finanziario selezionato', value: d.financial_indicators.var },
        { label: 'ESG', description: 'Indica il grado di allineamento del portafoglio in ambito sostenibilità, misurato tramite Rating ESG, rispetto a valori soglia definiti dalla Banca', value: d.financial_indicators.esg },
      ],
    };
  }, [data, isRebalancing, feeType]);

  // Stats for the "Caratteristiche di portafoglio" section; only present on
  // artifacts created after the backend started merging portfolio stats into
  // the composition data — older artifacts skip the section entirely.
  const statsData = useMemo(() => {
    const d = data?.data;
    return d?.sharpe != null ? d : null;
  }, [data]);

  // Determine grid class based on number of items
  const gridClass = useMemo(() => {
    const itemCount = actionItems.length;
    if (itemCount <= 1) return styles.gridSingle;
    if (itemCount <= 2) return styles.gridDouble;
    if (itemCount <= 3) return styles.gridTriple;
    return styles.gridQuad;
  }, [actionItems.length]);

  const { seedThread, sendMessage } = useStreetBeatChat();
  const actionInFlightRef = useRef(false);

  // Action handler. Rebalancing starters call seedThread to persist the user
  // starter + synthetic assistant question on the backend without triggering
  // a run (skip_run=true). The conversation pauses at the synthetic question
  // until the user types their actual reply, which goes through the regular
  // chat input → continueThread path. The diagnosi starter sends a
  // two-message split: the pill text stays visible to the advisor
  // (hidden_from_agent) while the agent receives an explicit diagnose
  // command (hidden) — the bare pill label alone intermittently missed the
  // semantic selection of the diagnose-flow hint and the agent answered in
  // plain text with no diagnosis artifact. Other actions send immediately.
  const handleActionClick = useCallback(
    (action: { id: string; text: string }) => {
      if (actionInFlightRef.current) return;
      actionInFlightRef.current = true;
      const guardWindow = new Promise((resolve) => setTimeout(resolve, ACTION_CLICK_GUARD_MS));
      const releaseAfter = (pending: Promise<unknown>) => {
        void Promise.all([pending, guardWindow]).then(() => {
          actionInFlightRef.current = false;
        });
      };
      if (
        action.id === 'ribilanciare' ||
        action.id === 'deposit' ||
        action.id === 'withdraw'
      ) {
        const id = action.id as FinecoRebalancingStarterId;
        const seedMessages = [
          { content: action.text, role: 'user' as const },
          {
            content: FINECO_REBALANCING_SYNTHETIC_QUESTIONS[id],
            role: 'assistant' as const,
            // `starter` lets ACE tell the rebalancing card (which already asked the
            // max-operations question) from the deposit/withdraw pills.
            metadata: { client_seeded: true, ...(id === 'ribilanciare' && { starter: id }) },
          },
        ];
        releaseAfter(
          seedThread(seedMessages).catch((err) =>
            log.error('seed rebalancing turn failed', { actionId: id, error: err }),
          ),
        );
        return;
      }
      if (action.id === 'diagnosi') {
        releaseAfter(
          sendMessage(buildDiagnosiMessages(action.text)).catch((err) =>
            notifySendFailure(
              'Failed to send diagnosi request',
              err,
              'La richiesta di diagnosi non è stata inviata. Riprova.',
            ),
          ),
        );
        return;
      }
      onSend(action.text);
      releaseAfter(Promise.resolve());
    },
    [onSend, seedThread, sendMessage],
  );

  if (!transformedData || transformedData.length === 0) {
    return null;
  }

  const polizzeSubtitle = polizzeSubTitle(polizzeData);

  return (
    <div className={styles.container}>
      {reportData && <EfficientFrontierContainer ef_data={ef_data} reportData={reportData} isOptimized={!isRebalancing} />}
      {statsData && <CaratteristicheDiPortafoglioContainer data={statsData} label={PortfolioConfigData.stats.label} />}
      {reportData && <IndicatoriFinanziariContainer reportData={reportData} label={PortfolioConfigData.indicatori_finanziari.label} />}
      <div className={styles.titleContainer}>
        <h3 className={styles.title}>Composizione del portafoglio</h3>
        <div className={styles.totalValueContainer}>
          <span className={styles.totalValueLabel}>Controvalore portafoglio</span>
          <span className={styles.totalValue}>€{formattedTotalControvalore}</span>
        </div>
      </div>

      <div className={styles.tablesContainer}>
        {/* Polizze Table */}
        {sortedPolizzeData.length > 0 && (
          <div className={styles.tableSectionContainer}>
            <h4 className={styles.sectionTitle}>{POLIZZE_TITLE}</h4>
            {polizzeSubtitle && <h5 className={styles.sectionTitle}>{polizzeSubtitle}</h5>}
            <div className={styles.tableContainer}>
              <PortfolioTable
                data={sortedPolizzeData}
                headers={FILTERED_POLIZZE.headers}
                sortConfig={polizzeSortConfig}
                columnMappings={COLUMN_MAPPINGS_POLIZZE}
                columnWidths={FILTERED_POLIZZE.widths}
                colorsConfig={colorsConfig}
                handleSort={handlePolizzeSort}
                onTooltipTrigger={handleTooltipTrigger}
                onTooltipHide={handleTooltipHide}
                sortableColumns={FILTERED_SORTABLE_POLIZZE}
                pinnedColumns={['isin', 'nome']}
              />
              <GlobalTooltip />
            </div>
            <div className={styles.subtotalContainer}>
              <span className={styles.subtotalLabel}>{POLIZZE_SUBTOTAL_LABEL}</span>
              <span className={styles.subtotalValue}>€{formattedPolizzeSubtotal}</span>
            </div>
          </div>
        )}
        {/* Funds, ETF, etc. Table */}
        {sortedFundsEtfData.length > 0 && (
          <div className={styles.tableSectionContainer}>
            <h4 className={styles.sectionTitle}>Fondi, SICAV, ETF, ETC, ETN</h4>
            <div className={styles.tableContainer}>
              <PortfolioTable
                data={sortedFundsEtfData}
                headers={filteredFondi.headers}
                sortConfig={fundsEtfSortConfig}
                columnMappings={COLUMN_MAPPINGS_FONDI}
                columnWidths={filteredFondi.widths}
                colorsConfig={colorsConfig}
                handleSort={handleFundsEtfSort}
                onTooltipTrigger={handleTooltipTrigger}
                onTooltipHide={handleTooltipHide}
                sortableColumns={filteredSortableFondi}
                pinnedColumns={['isin', 'nome']}
              />
              <GlobalTooltip />
            </div>

            <div className={styles.subtotalContainer}>
              <span className={styles.subtotalLabel}>Totale controvalore portafoglio Fondi, SICAV, ETF, ETC, ETN</span>
              <span className={styles.subtotalValue}>€{formattedFundsEtfSubtotal}</span>
            </div>
          </div>
        )}

        {/* Bonds Table */}
        {sortedBondsData.length > 0 && (
          <div className={styles.tableSectionContainer}>
            <h4 className={styles.sectionTitle}>Titoli Obbligazionari</h4>
            <div className={styles.tableContainer}>
              <PortfolioTable
                data={sortedBondsData}
                headers={FILTERED_BONDS.headers}
                sortConfig={bondsSortConfig}
                columnMappings={COLUMN_MAPPINGS_BONDS}
                columnWidths={FILTERED_BONDS.widths}
                colorsConfig={colorsConfig}
                handleSort={handleBondsSort}
                onTooltipTrigger={handleTooltipTrigger}
                onTooltipHide={handleTooltipHide}
                sortableColumns={FILTERED_SORTABLE_BONDS}
                pinnedColumns={['isin', 'nome']}
              />
              <GlobalTooltip />
            </div>

            <div className={styles.subtotalContainer}>
              <span className={styles.subtotalLabel}>Totale controvalore portafoglio Obbligazioni</span>
              <span className={styles.subtotalValue}>€{formattedBondsSubtotal}</span>
            </div>
          </div>
        )}

        {/* Stocks Table */}
        {sortedStocksData.length > 0 && (
          <div className={styles.tableSectionContainer}>
            <h4 className={styles.sectionTitle}>Titoli Azionari</h4>
            <div className={styles.tableContainer}>
              <PortfolioTable
                data={sortedStocksData}
                headers={FILTERED_STOCKS.headers}
                sortConfig={stocksSortConfig}
                columnMappings={COLUMN_MAPPINGS_STOCKS}
                columnWidths={FILTERED_STOCKS.widths}
                colorsConfig={colorsConfig}
                handleSort={handleStocksSort}
                onTooltipTrigger={handleTooltipTrigger}
                onTooltipHide={handleTooltipHide}
                sortableColumns={FILTERED_SORTABLE_STOCKS}
                pinnedColumns={['isin', 'nome']}
              />
              <GlobalTooltip />
            </div>

            <div className={styles.subtotalContainer}>
              <span className={styles.subtotalLabel}>Totale controvalore portafoglio Azioni</span>
              <span className={styles.subtotalValue}>€{formattedStocksSubtotal}</span>
            </div>
          </div>
        )}

        {/* Certificati Table */}
        {sortedCertificatiData.length > 0 && (
          <div className={styles.tableSectionContainer}>
            <h4 className={styles.sectionTitle}>Certificati</h4>
            <div className={styles.tableContainer}>
              <PortfolioTable
                data={sortedCertificatiData}
                headers={FILTERED_CERTIFICATI.headers}
                sortConfig={certificatiSortConfig}
                columnMappings={COLUMN_MAPPINGS_CERTIFICATI}
                columnWidths={FILTERED_CERTIFICATI.widths}
                colorsConfig={colorsConfig}
                handleSort={handleCertificatiSort}
                onTooltipTrigger={handleTooltipTrigger}
                onTooltipHide={handleTooltipHide}
                sortableColumns={FILTERED_SORTABLE_CERTIFICATI}
                pinnedColumns={['isin', 'nome']}
              />
              <GlobalTooltip />
            </div>
            <div className={styles.subtotalContainer}>
              <span className={styles.subtotalLabel}>Totale controvalore portafoglio Certificati</span>
              <span className={styles.subtotalValue}>€{formattedCertificatiSubtotal}</span>
            </div>
          </div>
        )}

      </div>

      <div className={styles.wrapper}>
        <div className={`${styles.gridContainer} ${gridClass}`}>
          {actionItems.map((action) => (
            <div key={action.id} className={styles.gridItemContainer} onClick={() => handleActionClick(action)}>
              <div className={styles.gridItem}>
                {action.icon}
                {action.text}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FinecoPortfolioCompositionArtifact;
